import { useState, useEffect, useRef, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/ui/Button';
import type { ProjectTask, TaskComment } from '@/types/task';
import { addMessageToTask, editVideoTask } from '../../../apiServices/taskApi';
import {
  addMessageToTask as addMessageToContext,
  updateTaskDuration,
} from '../../../app/features/project/projectSlice';
import { formatMilliSeconds } from '../../../util/UtilFunctions';
import { isLineType } from '../lib/lineMath';
import { TaskDot } from './TaskDot';
import { Message } from './Message';

interface TaskPopoverProps {
  task: ProjectTask;
  onClose: () => void;
  /** Update the task in parent state (e.g. after adding a comment) */
  onUpdateTask?: (task: ProjectTask) => void;
  /** Seek video to a time (for duration editing) */
  onSeek?: (seconds: number) => void;
  mediaType: 'video' | 'audio' | 'image';
}

/** Position the popover near the task's coordinate */
function getPopoverPosition(task: ProjectTask, mediaType: string) {
  const x = Number(task.coord_x ?? task.coordX ?? 50);
  const y = Number(task.coord_y ?? task.coordY ?? 50);

  if (mediaType === 'audio') {
    return { top: 0, left: 0, bottom: 0, right: 0, margin: 'auto', height: 'fit-content' } as const;
  }

  // For both video and image: position near the task dot
  let offsetTop: number;
  let offsetLeft: number;

  if (y > 66) offsetTop = 18;
  else if (y < 33) offsetTop = 5;
  else offsetTop = 10;

  if (x > 50) offsetLeft = 27;
  else offsetLeft = -2;

  return {
    top: `calc(${y}% - ${offsetTop}rem)`,
    left: `calc(${x}% - ${offsetLeft}rem)`,
  };
}

export function TaskPopover({ task, onClose, onUpdateTask, onSeek, mediaType }: TaskPopoverProps) {
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const user = useAppSelector((state) => state.user);
  const projectUsers = useAppSelector((state) => state.project.users) as Array<{ uid: string; [k: string]: unknown }>;
  const userRole = useAppSelector((state) => state.project.userRole) as string | null;
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [commentText, setCommentText] = useState('');
  const [commentSending, setCommentSending] = useState(false);
  const [duration, setDuration] = useState(() => parseFloat(String(task.duration)) || 1);
  const [durationSaving, setDurationSaving] = useState(false);

  const isArrowType = isLineType(task.taskType);
  const showDuration = mediaType === 'video' && isArrowType;

  // Close on mousedown outside — ignore clicks within 200ms of mount to avoid
  // the triggering mouseup from immediately closing the popover
  const mountedAt = useRef(Date.now());
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (Date.now() - mountedAt.current < 200) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [onClose]);

  const handleSubmitComment = async (e: FormEvent) => {
    e.preventDefault();
    const text = commentText.trim();
    if (!text) {
      notify({ id: String(Date.now()), type: 'info', message: 'Form is not complete' });
      return;
    }

    // Optimistic: show immediately
    const newMsg: TaskComment = {
      content: text,
      created: new Date().toString(),
      sender: user.uid,
      userData: projectUsers.find((u) => u.uid === user.uid) as TaskComment['userData'],
    };
    dispatch(addMessageToContext({ task, message: newMsg }));
    const updatedTask = { ...task, messages: [...task.messages, newMsg] };
    onUpdateTask?.(updatedTask);
    setCommentText('');
    setCommentSending(true);

    try {
      await addMessageToTask(task.id, text);
    } catch {
      // Revert: restore original task (without the optimistic message)
      dispatch(updateTaskDuration(task));
      onUpdateTask?.(task);
      setCommentText(text);
      notify({ id: String(Date.now()), type: 'error', message: 'Could not send message' });
    }
    setCommentSending(false);
  };

  const handleDurationChange = (val: number) => {
    setDuration(val);
    if (onSeek && task.timestamp != null) {
      const startSec = Math.round((task.timestamp / 1000) * 10) / 10;
      onSeek(startSec + val);
    }
  };

  const handleSaveDuration = async () => {
    setDurationSaving(true);
    try {
      await editVideoTask({ id: task.id, duration, status: task.status, tasktype: task.taskType });
      dispatch(updateTaskDuration({ ...task, duration }));
    } catch {
      notify({ id: String(Date.now()), type: 'error', message: 'Could not update task' });
    }
    setDurationSaving(false);
  };

  const positionStyle = getPopoverPosition(task, mediaType);

  return (
    <div className={`absolute inset-0 z-[6] ${mediaType}`}>
      <div
        ref={wrapperRef}
        className="absolute z-10 w-[25rem] max-w-[95%] max-h-[80%] flex flex-col rounded-xl bg-card shadow-2xl border border-border/60 overflow-hidden"
        style={positionStyle}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-light px-4 py-2">
          <h3 className="text-sm font-semibold text-foreground">
            Task {task.index}
            {task.type !== 'image' && task.timestamp != null && (
              <span className="ml-1 font-normal text-muted-foreground">
                ({formatMilliSeconds(task.timestamp)})
              </span>
            )}
          </h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-accent-foreground">
            <X className="size-4" />
          </button>
        </div>

        {/* Duration editor (video arrows only) */}
        {showDuration && (
          <div className="flex items-center gap-2 border-b border-border-light px-4 py-2">
            <label className="text-xs text-muted-foreground">Duration:</label>
            <input
              type="number"
              min={0.5}
              max={100}
              step={0.5}
              value={duration}
              onChange={(e) => handleDurationChange(parseFloat(e.target.value) || 0.5)}
              className="w-16 rounded border border-border px-2 py-0.5 text-xs"
            />
            <span className="text-xs text-muted-foreground">s</span>
            {duration !== parseFloat(String(task.duration)) && (
              <Button size="sm" onClick={handleSaveDuration} disabled={durationSaving}>
                {durationSaving ? '...' : 'Update'}
              </Button>
            )}
          </div>
        )}

        {/* Messages list */}
        <div className="flex-1 overflow-y-auto">
          {task.messages.map((msg, i) => (
            <Message
              key={msg.created}
              firstName={msg.userData?.given_name}
              lastName={msg.userData?.family_name}
              created={msg.created}
              content={msg.content}
              image={{ task: false, imageUrl: msg.userData?.img }}
              isAReply={i !== 0}
              isRemovable={false}
              projectId=""
              updateModalOpen={() => {}}
            />
          ))}
        </div>

        {/* Comment form */}
        {userRole !== 'guest' && (
          <form onSubmit={handleSubmitComment} className="border-t border-border-light p-3">
            <textarea
              className="w-full resize-none rounded border border-border p-2 text-sm outline-none placeholder:text-muted-foreground focus:border-info"
              placeholder="Add new comment"
              rows={2}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />
            <div className="mt-1 flex justify-end">
              <Button type="submit" size="sm" disabled={commentSending}>
                {commentSending ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
